import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface CachedPixel {
  id: string;
  user_id: string;
  ad_account_id: string;
  pixel_id: string;
  pixel_name: string;
  display_name: string;
  last_synced_at: string | null;
  created_at: string | null;
}

export function useCachedPixels(adAccountId: string | null | undefined, options?: { refetchInterval?: number | false | ((query: any) => number | false) }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const query = useQuery({
    queryKey: ["cached-pixels", userId, adAccountId],
    queryFn: async (): Promise<CachedPixel[]> => {
      if (!userId || !adAccountId) return [];

      const { data, error } = await supabase
        .from("meta_cached_pixels")
        .select("*")
        .eq("user_id", userId)
        .eq("ad_account_id", adAccountId)
        .order("pixel_name");

      if (error) throw error;
      return (data ?? []) as CachedPixel[];
    },
    enabled: !!userId && !!adAccountId,
    staleTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["cached-pixels", userId, adAccountId] });
  };

  return { ...query, invalidate };
}
